import type { ExperienceItem } from "@/lib/types";
import type { RheaEvents } from "@/lib/analytics";
import { passesMaxPrice } from "./priceFilter";
import { isMarketplaceEligibleProvider, normalizeProviderKey } from "./providerLinkState";

// ─── Marketplace filter + sort — pure logic (Marketplace Filter + Sorting
// Confidence V1) ─────────────────────────────────────────────────────────
// Extracted out of routes/experiences.tsx (previously private, unexported,
// untestable `_applyFilters`/`_sortExperiences`/`_hasActiveFilters`
// functions) so the null-handling and ordering rules this sprint audited
// can be unit-tested directly, the same way priceFilter.ts/
// comparisonDecision.ts already are. Both FilterBar and
// ItineraryFilterChipPanel (mobile drawer and desktop rail) import the
// same ActiveFilters type and helpers from here — there is exactly one
// definition of "what counts as an active filter" and "how each sort
// orders items", never two copies that could quietly drift apart.

export type DurationFilter = "short" | "medium" | "long";
export type SortBy = "default" | "duration_asc" | "price_asc" | "provider_first";

// ─── Marketplace Service-Type Reachability V1 ───────────────────────────────
// A discrete 3-way view, not a generic multi-select like `providers` — the
// brief asks for exactly "Experiencias" / "Traslados y servicios" / "Todo".
// "experience" maps to service_types=[] (today's literal default, not an
// explicit ["experience"]) so an untouched control never looks like an
// active filter or mints a different cache key from before this sprint.
export type ServiceTypeView = "experience" | "transfers" | "all";

export const SERVICE_TYPES_BY_VIEW: Record<ServiceTypeView, string[]> = {
  experience: [],
  transfers:  ["transfer", "airport_service"],
  all:        ["experience", "transfer", "airport_service", "ticket"],
};

export interface ActiveFilters {
  categories:   Set<string>;   // empty = all
  duration:     DurationFilter | null;
  timeOfDay:    string | null; // morning | afternoon | evening | night | null
  reservable:   boolean;       // only availability_confirmed=true
  providerOnly: boolean;       // only LIVE_PROVIDER (exclude editorial)
  /**
   * Marketplace Filters V2 — "show me activities up to this maximum amount",
   * NOT a total-trip budget (that's a separate, Planner-only concept — see
   * FilterSidebar). null = "Sin límite" / no filter active.
   */
  maxPrice:     number | null;
  /**
   * Marketplace Continuity, Honest Pricing & Log Privacy V1, Parte B —
   * "Incluir experiencias de aporte voluntario" explicit opt-in. Only has
   * any effect while maxPrice is active (see passesMaxPrice()) — a
   * tip_based item (e.g. GuruWalk) is never assumed to fit the ceiling,
   * the user must ask for it explicitly.
   */
  includeTipBased: boolean;
  sortBy:       SortBy;
  /**
   * Full-Width Marketplace Grid & Scalable Provider Filter V1 — canonical
   * provider keys (normalizeProviderKey()'d), applied over the FULL pool
   * server-side (see marketplace_search_filters.apply_search_filters's
   * own `providers` param) — this client-side copy only mirrors that same
   * rule for legacy surfaces that still filter locally. Empty Set = "Todos
   * los proveedores".
   */
  providers:    Set<string>;
  /**
   * Marketplace Service-Type Reachability V1 — "experience" (default) |
   * "transfers" | "all". See SERVICE_TYPES_BY_VIEW for the exact
   * service_types array each view sends server-side.
   */
  serviceTypeView: ServiceTypeView;
  /**
   * Sprint: Filter Taxonomy + Dynamic Result Filters V1 — canonical
   * filter-taxonomy group ids the user explicitly selected as chips (e.g.
   * "nature_outdoors", "beach_water"), applied over the FULL pool
   * server-side (see marketplace_search_filters.apply_search_filters's
   * own `filter_taxonomy` param) — same multi-select Set<string> pattern
   * as `providers` above. Empty Set = no chip selected (today's default,
   * unaffected). Independent of whatever the free-text query itself
   * implies (see response.filter_taxonomy.requested_filter_groups) — the
   * two are never conflated.
   */
  filterTaxonomy: Set<string>;
  /**
   * Hotfix/Sprint: Language Selector Marketplace Wiring V1 — the request's
   * `market_locale` ("es-CO" | "en-US" | "pt-BR"), kept in sync with the
   * header's ES/EN/PT selector (usePreferences().language via
   * languageToMarketLocale()) by MarketplaceShell/ExperienceTimeline's own
   * effect, never set directly by a user-facing filter control. Included in
   * marketplaceFiltersKey() so a language switch always re-fetches page 1 —
   * but deliberately EXCLUDED from hasActiveFilters()/countActiveFilters()
   * and untouched by "clear filters": it's a standing language preference,
   * not a filter a "Limpiar" action should ever reset.
   */
  marketLocale: string;
}

/** Hotfix/Sprint: Language Selector Marketplace Wiring V1 — matches the
 *  backend's own default (main.py: `req.market_locale or "es-CO"`). Any real
 *  mount immediately overwrites this via languageToMarketLocale(language) —
 *  see MarketplaceShell.tsx/ExperienceTimeline's own lazy initializer. */
export const DEFAULT_MARKET_LOCALE = "es-CO";

export const EMPTY_FILTERS: ActiveFilters = {
  categories:   new Set(),
  duration:     null,
  timeOfDay:    null,
  reservable:   false,
  providerOnly: false,
  maxPrice:     null,
  includeTipBased: false,
  sortBy:       "default",
  providers:    new Set(),
  serviceTypeView: "experience",
  filterTaxonomy: new Set(),
  marketLocale: DEFAULT_MARKET_LOCALE,
};

/**
 * Marketplace Grid Behavioral Correctness V1.1, Parte E — a genuinely
 * fresh ActiveFilters object with its OWN new categories/providers Sets.
 * Never `{ ...EMPTY_FILTERS }` alone: that would copy EMPTY_FILTERS' own
 * Set instances by reference, so mutating the "empty" result anywhere
 * would silently mutate the shared module-level constant for every other
 * caller too. Every "clear filters" action should call this, never reuse
 * a Set from elsewhere.
 */
export function createEmptyMarketplaceFilters(): ActiveFilters {
  return {
    ...EMPTY_FILTERS,
    categories: new Set(),
    providers:  new Set(),
    filterTaxonomy: new Set(),
  };
}

export function hasActiveFilters(f: ActiveFilters): boolean {
  return (
    f.categories.size > 0 ||
    f.duration !== null ||
    f.timeOfDay !== null ||
    f.reservable ||
    f.providerOnly ||
    f.maxPrice !== null ||
    f.sortBy !== "default" ||
    f.providers.size > 0 ||
    f.serviceTypeView !== "experience" ||
    f.filterTaxonomy.size > 0
  );
}

/**
 * How many independent refinements are active — shown as the "Limpiar (N)"
 * badge on both the mobile drawer and desktop rail. Sort counts as one of
 * these refinements (consistent with "Recomendadas" being a real, distinct
 * choice among several, not a hidden default) — deliberately the SAME rule
 * hasActiveFilters() uses for "is anything non-default active at all", so
 * the two can never disagree about whether the drawer should show a
 * "Limpiar" affordance in the first place.
 */
export function countActiveFilters(f: ActiveFilters): number {
  return (
    f.categories.size
    + (f.duration    !== null ? 1 : 0)
    + (f.timeOfDay   !== null ? 1 : 0)
    + (f.reservable            ? 1 : 0)
    + (f.providerOnly          ? 1 : 0)
    + (f.maxPrice    !== null ? 1 : 0)
    // Only counts as its own refinement while it actually does something —
    // includeTipBased has zero effect when maxPrice is null (see
    // passesMaxPrice()), so it must never inflate the badge on its own.
    + (f.maxPrice !== null && f.includeTipBased ? 1 : 0)
    + (f.sortBy !== "default"  ? 1 : 0)
    + f.providers.size
    + (f.serviceTypeView !== "experience" ? 1 : 0)
    + f.filterTaxonomy.size
  );
}

export function matchesDuration(exp: ExperienceItem, d: DurationFilter): boolean {
  const h = exp.duration_hours ?? 0;
  if (d === "short")  return h > 0 && h < 2;
  if (d === "medium") return h >= 2 && h <= 4;
  if (d === "long")   return h > 4;
  return true;
}

export function applyFilters(
  items: ExperienceItem[],
  f:     ActiveFilters,
): ExperienceItem[] {
  return items.filter(exp => {
    if (f.categories.size > 0 && !f.categories.has(exp.category))                      return false;
    if (f.duration !== null    && !matchesDuration(exp, f.duration))                    return false;
    if (f.timeOfDay !== null   && exp.time_of_day !== f.timeOfDay)                      return false;
    if (f.reservable           && exp.availability_confirmed !== true)                  return false;
    if (f.providerOnly         && exp.source_class !== "LIVE_PROVIDER")                 return false;
    if (!passesMaxPrice(exp, f.maxPrice, f.includeTipBased))                            return false;
    if (f.providers.size > 0 && !f.providers.has(normalizeProviderKey(exp.provider_name))) return false;
    return true;
  });
}

/**
 * Ordering rules (Marketplace Filter + Sorting Confidence V1, task 3):
 *   - "default": never called with this value — every call site already
 *     guards `sortBy !== "default"` before invoking sortExperiences(), so
 *     the real "Recomendadas" order comes from the caller's own
 *     quality-aware ranking (the backend's _marketplace_rank_key when a
 *     live search is active, or getRheaRecommendationRank() as the
 *     client-side fallback) — never plain insertion order. Returning the
 *     input unchanged here is a defensive no-op, not the actual behavior.
 *   - "price_asc"/"duration_asc": missing values sort LAST (a large
 *     sentinel), never first — an unknown price/duration is not "the
 *     cheapest"/"the shortest", it's unknown, so it must never look like
 *     the best match for an ascending sort.
 *   - "provider_first": prioritizes ACTIONABLE items — a real, currently
 *     bookable provider link (isMarketplaceEligibleProvider), not merely
 *     "is tagged LIVE_PROVIDER" — a provider item with no valid booking
 *     link isn't more useful to open than a well-formed local reference.
 */
export function sortExperiences(items: ExperienceItem[], sort: SortBy): ExperienceItem[] {
  if (sort === "default") return items;
  const copy = [...items];
  if (sort === "duration_asc") {
    return copy.sort((a, b) => (a.duration_hours ?? 999) - (b.duration_hours ?? 999));
  }
  if (sort === "price_asc") {
    return copy.sort((a, b) => (a.price_from ?? 999999) - (b.price_from ?? 999999));
  }
  if (sort === "provider_first") {
    return copy.sort((a, b) => {
      const aActionable = isMarketplaceEligibleProvider(a) ? 0 : 1;
      const bActionable = isMarketplaceEligibleProvider(b) ? 0 : 1;
      if (aActionable !== bActionable) return aActionable - bActionable;
      // Within the same actionable/not-actionable bucket, still prefer a
      // LIVE_PROVIDER item over a local/reference one — same tie-break the
      // previous implementation used as its only rule.
      const aP = a.source_class === "LIVE_PROVIDER" ? 0 : 1;
      const bP = b.source_class === "LIVE_PROVIDER" ? 0 : 1;
      return aP - bP;
    });
  }
  return items;
}

// ─── Launch Analytics & Funnel Instrumentation V1 ──────────────────────────
// Pure builders for the marketplace_filter_applied/marketplace_sort_changed
// payloads — colocated with applyFilters/countActiveFilters since
// result_count_after/active_filter_count/result_count are computed directly
// from them. Kept as plain builders (never calling track() themselves) so
// they're directly unit-testable, the same convention detailAnalytics.ts's
// buildDetailEventProps already uses.

export function buildFilterAppliedProps(
  filterType:      RheaEvents["marketplace_filter_applied"]["filter_type"],
  filterValue:     string,
  allItems:        ExperienceItem[],
  nextFilters:     ActiveFilters,
  destinationIata: string | null | undefined,
): RheaEvents["marketplace_filter_applied"] {
  return {
    filter_type:         filterType,
    filter_value:        filterValue,
    active_filter_count: countActiveFilters(nextFilters),
    result_count_after:  applyFilters(allItems, nextFilters).length,
    destination_iata:    destinationIata || undefined,
  };
}

export function buildSortChangedProps(
  sortKey:         SortBy,
  allItems:        ExperienceItem[],
  currentFilters:  ActiveFilters,
  destinationIata: string | null | undefined,
): RheaEvents["marketplace_sort_changed"] {
  return {
    sort_key:          sortKey,
    // Sorting never adds/removes items — the count is the current filtered
    // result set, unaffected by which sort is now active.
    result_count:      applyFilters(allItems, currentFilters).length,
    destination_iata:  destinationIata || undefined,
  };
}

// ─── Marketplace Grid Behavioral Correctness V1.1, Parte D ─────────────────
// buildFilterAppliedProps() above computes result_count_after from
// `allItems` (the currently-loaded PAGE) via applyFilters() — correct for
// every other filter dimension, but wrong for `providers`: the page in
// memory reflects the PREVIOUS provider selection (or none), so switching
// Viator -> GuruWalk could report 0 even though GuruWalk has real
// inventory. This computes it instead from facets.provider_counts — a
// pool-wide count already computed server-side BEFORE the provider filter
// itself (see marketplace_search_filters.build_facets) — so the number is
// correct the instant the user clicks, before the new page even arrives.

/**
 * providers empty ("Todos") sums every known provider's count; one or more
 * selected sums exactly those; a selected key with no entry in
 * providerCounts contributes 0 (never throws, never invents a count).
 */
export function computeProviderFilterResultCount(
  providers:      Set<string>,
  providerCounts: Record<string, number>,
): number {
  const counted = providers.size === 0 ? Object.keys(providerCounts) : Array.from(providers);
  return counted.reduce((sum, key) => sum + (providerCounts[key] ?? 0), 0);
}

/**
 * The ONE builder for the provider filter's marketplace_filter_applied
 * payload — never routes through buildFilterAppliedProps' generic
 * allItems-based math. filter_value is "all" for the empty-Set ("Todos")
 * case, else the selected-provider COUNT as a string — never a provider
 * name, query, title, URL, cursor, or any other identifying value.
 */
export function buildProviderFilterAppliedProps(
  providers:       Set<string>,
  providerCounts:  Record<string, number>,
  nextFilters:     ActiveFilters,
  destinationIata: string | null | undefined,
): RheaEvents["marketplace_filter_applied"] {
  return {
    filter_type:         "provider",
    filter_value:        providers.size === 0 ? "all" : String(providers.size),
    active_filter_count: countActiveFilters(nextFilters),
    result_count_after:  computeProviderFilterResultCount(providers, providerCounts),
    destination_iata:    destinationIata || undefined,
  };
}

// ─── Marketplace Service-Type Reachability V1 ───────────────────────────────
// Same "compute from facets, not from the currently-loaded page" pattern as
// the provider filter above — facets.service_type_counts is a pool-wide
// count already computed server-side with service_types held open to every
// known type, so the number is correct the instant the user clicks, before
// the new page even arrives.

/**
 * Sums facets.service_type_counts over exactly the types `view` will
 * request. "experience" maps to service_types=[] server-side, which the
 * backend treats as ["experience"] — mirrored here so the displayed count
 * for the default view is never computed against an empty type list.
 */
export function computeServiceTypeFilterResultCount(
  view:              ServiceTypeView,
  serviceTypeCounts: Record<string, number>,
): number {
  const types = SERVICE_TYPES_BY_VIEW[view].length > 0 ? SERVICE_TYPES_BY_VIEW[view] : ["experience"];
  return types.reduce((sum, key) => sum + (serviceTypeCounts[key] ?? 0), 0);
}

/**
 * The ONE builder for the service-type filter's marketplace_filter_applied
 * payload. filter_value is the view name itself ("experience" |
 * "transfers" | "all") — never a query, title, URL, or cursor.
 */
export function buildServiceTypeFilterAppliedProps(
  view:              ServiceTypeView,
  serviceTypeCounts: Record<string, number>,
  nextFilters:       ActiveFilters,
  destinationIata:   string | null | undefined,
): RheaEvents["marketplace_filter_applied"] {
  return {
    filter_type:         "service_type",
    filter_value:        view,
    active_filter_count: countActiveFilters(nextFilters),
    result_count_after:  computeServiceTypeFilterResultCount(view, serviceTypeCounts),
    destination_iata:    destinationIata || undefined,
  };
}

// ─── Sprint: Filter Taxonomy + Dynamic Result Filters V1 ───────────────────
// Same "compute from facets, not from the currently-loaded page" pattern as
// the provider/service-type filters above — facets.filter_taxonomy_counts
// is a pool-wide count already computed server-side with the filter_
// taxonomy chip selection held open, so the number is correct the instant
// the user clicks, before the new page even arrives.

/**
 * groups empty ("todos los tipos") sums every known group's count — but
 * unlike providers, groups OVERLAP (one item can count toward more than
 * one group), so this is only ever an upper-bound estimate for the
 * empty-selection case, never an exact "total items" figure. One or more
 * selected groups sums exactly those; a selected id with no entry simply
 * contributes 0.
 */
export function computeFilterTaxonomyFilterResultCount(
  groups:              Set<string>,
  filterTaxonomyCounts: Record<string, number>,
): number {
  const counted = groups.size === 0 ? Object.keys(filterTaxonomyCounts) : Array.from(groups);
  return counted.reduce((sum, key) => sum + (filterTaxonomyCounts[key] ?? 0), 0);
}

/**
 * The ONE builder for the filter-taxonomy chip's marketplace_filter_applied
 * payload. filter_value is "all" for the empty-Set case, else the
 * selected-group COUNT as a string — never a group label, query, title,
 * URL, or cursor.
 */
export function buildFilterTaxonomyFilterAppliedProps(
  groups:               Set<string>,
  filterTaxonomyCounts: Record<string, number>,
  nextFilters:          ActiveFilters,
  destinationIata:      string | null | undefined,
): RheaEvents["marketplace_filter_applied"] {
  return {
    filter_type:         "filter_taxonomy",
    filter_value:        groups.size === 0 ? "all" : String(groups.size),
    active_filter_count: countActiveFilters(nextFilters),
    result_count_after:  computeFilterTaxonomyFilterResultCount(groups, filterTaxonomyCounts),
    destination_iata:    destinationIata || undefined,
  };
}
